import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, CameraOff, X } from "lucide-react";

import { Button } from "./ui/button";

/**
 * Phone-camera QR reader for the check-in station.
 *
 * Reads the ticket QR (which encodes the ticket code) and hands the code to the
 * same submit path a hardware scanner or the keyboard uses — the camera is just
 * another way to type the code.
 *
 * Decoding prefers the browser's native BarcodeDetector (Chrome on Android,
 * fast, off the main thread) and falls back to jsQR on a canvas for browsers
 * without it (iPhone Safari). Both run in the page; nothing is uploaded.
 *
 * The camera stays open after a read so the next person in the queue can be
 * scanned without tapping anything. A 2.5s cooldown per code stops one ticket
 * held in front of the lens from being read twenty times.
 */

const COOLDOWN_MS = 2500;

export function extractTicketCode(text) {
  // Tickets encode the bare code. Tolerate a full ticket URL too, in case a
  // future ticket or a screenshot of the ticket page carries the link instead.
  const raw = String(text || "").trim();
  if (!raw) return "";
  const path = raw.split(/[?#]/)[0];
  const last = path.split("/").filter(Boolean).pop() || raw;
  const cleaned = last.toUpperCase().replace(/[^A-Z0-9]/g, "");
  return cleaned.length === 8 ? cleaned : "";
}

export function cameraSupported() {
  return typeof navigator !== "undefined"
    && !!navigator.mediaDevices
    && typeof navigator.mediaDevices.getUserMedia === "function";
}

export default function CameraScanner({ onCode, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const frameRef = useRef(0);
  const lastRef = useRef({ code: "", at: 0 });
  // The parent re-renders after every scan result; the camera must not restart
  // each time, so the callback is read through a ref instead of a dependency.
  const onCodeRef = useRef(onCode);
  useEffect(() => { onCodeRef.current = onCode; }, [onCode]);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  const stop = useCallback(() => {
    cancelAnimationFrame(frameRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const handleText = useCallback((text) => {
    const code = extractTicketCode(text);
    if (!code) return;
    const now = Date.now();
    if (lastRef.current.code === code && now - lastRef.current.at < COOLDOWN_MS) return;
    lastRef.current = { code, at: now };
    if (navigator.vibrate) navigator.vibrate(60);
    onCodeRef.current(code);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let detector = null;
    let jsQR = null;

    async function start() {
      if (!cameraSupported()) {
        setError("This browser can't use the camera. Type the code instead.");
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const video = videoRef.current;
        video.srcObject = stream;
        await video.play();
        setReady(true);
      } catch (e) {
        setError(e?.name === "NotAllowedError"
          ? "Camera permission was refused. Allow it in the browser, or type the code."
          : "Could not start the camera. Type the code instead.");
        return;
      }

      if ("BarcodeDetector" in window) {
        try {
          detector = new window.BarcodeDetector({ formats: ["qr_code"] });
        } catch { detector = null; }
      }
      if (!detector) {
        jsQR = (await import("jsqr")).default;
      }

      const tick = async () => {
        if (cancelled) return;
        const video = videoRef.current;
        if (video && video.readyState >= 2) {
          try {
            if (detector) {
              const found = await detector.detect(video);
              if (found?.length) handleText(found[0].rawValue);
            } else if (jsQR) {
              const canvas = canvasRef.current;
              const w = video.videoWidth, h = video.videoHeight;
              if (w && h) {
                canvas.width = w; canvas.height = h;
                const ctx = canvas.getContext("2d", { willReadFrequently: true });
                ctx.drawImage(video, 0, 0, w, h);
                const img = ctx.getImageData(0, 0, w, h);
                const hit = jsQR(img.data, w, h, { inversionAttempts: "dontInvert" });
                if (hit?.data) handleText(hit.data);
              }
            }
          } catch {
            // A single bad frame is not an error worth showing at a door.
          }
        }
        frameRef.current = requestAnimationFrame(tick);
      };
      frameRef.current = requestAnimationFrame(tick);
    }

    start();
    return () => { cancelled = true; stop(); };
  }, [handleText, stop]);

  return (
    <div className="relative rounded-xl overflow-hidden bg-black" data-testid="camera-scanner">
      <video ref={videoRef} playsInline muted autoPlay
             className="w-full aspect-[4/3] object-cover"
             aria-label="Camera view for scanning tickets" />
      <canvas ref={canvasRef} className="hidden" />

      {/* Framing guide, so people know where to hold the ticket. */}
      {ready && !error && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="w-3/5 aspect-square rounded-2xl border-4 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-6 text-center bg-black/80 text-white"
             data-testid="camera-error">
          <CameraOff className="h-8 w-8" aria-hidden="true" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {!ready && !error && (
        <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
          <Camera className="h-5 w-5 mr-2 animate-pulse" aria-hidden="true" />Starting camera…
        </div>
      )}

      <Button size="sm" variant="secondary" onClick={onClose}
              className="absolute top-2 right-2 h-9 gap-1.5"
              data-testid="camera-close">
        <X className="h-4 w-4" aria-hidden="true" />Stop camera
      </Button>
    </div>
  );
}
